/**
 * Sohbet paneli — sağdan açılır, ekranlar açıkken çalışmaya devam edilir. Geniş panelde
 * konuşma listesi ve açık konuşma yan yana; dar panelde ve mobilde tek tek (geri düğmesiyle).
 * Genişlik sol kenardan sürüklenir ve hatırlanır.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { IconButton } from '@/shared/ui/IconButton';
import { useBreakpoint } from '@/shared/hooks/useBreakpoint';
import { useChatStore } from '../model/chatStore';
import { ConversationList } from './ConversationList';
import { ThreadView } from './ThreadView';

const SPLIT_MIN = 680;

export function ChatPanel() {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const width = useChatStore((s) => s.width);
  const setWidth = useChatStore((s) => s.setWidth);
  const setOpen = useChatStore((s) => s.setOpen);
  const activeKey = useChatStore((s) => s.activeKey);
  const openConversation = useChatStore((s) => s.openConversation);
  const split = !isMobile && width >= SPLIT_MIN;

  const drag = useRef<{ x: number; w: number } | null>(null);
  useEffect(() => {
    const move = (e: PointerEvent) => drag.current && setWidth(drag.current.w + (drag.current.x - e.clientX));
    const up = () => {
      drag.current = null;
      document.body.style.userSelect = '';
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [setWidth]);

  // Esc paneli kapatır (bir pencere/menü açıksa onlar önce kapanır).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && !document.querySelector('[role="dialog"]')) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  return (
    <aside
      role="complementary"
      aria-label={t('chat.title')}
      data-chat-panel
      className={cn('bg-bg-elevated border-border fixed z-40 flex flex-col shadow-2xl', isMobile ? 'inset-0' : 'inset-y-0 right-0 border-l')}
      style={isMobile ? undefined : { width }}
    >
      {!isMobile ? (
        <div
          role="separator"
          aria-orientation="vertical"
          onPointerDown={(e) => {
            drag.current = { x: e.clientX, w: width };
            document.body.style.userSelect = 'none';
          }}
          className="hover:bg-brand-500/40 absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize"
        />
      ) : null}
      <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-3">
        <MessageSquare className="text-brand-600 h-5 w-5" />
        <span className="flex-1 text-sm font-semibold">{t('chat.title')}</span>
        <IconButton label={t('chat.close')} size="sm" onClick={() => setOpen(false)}>
          <X className="h-4 w-4" />
        </IconButton>
      </div>
      <div className="flex min-h-0 flex-1">
        {split || !activeKey ? (
          <div className={cn('flex min-h-0 flex-col', split ? 'border-border w-[300px] shrink-0 border-r' : 'flex-1')}>
            <ConversationList onOpen={openConversation} />
          </div>
        ) : null}
        {activeKey ? (
          <ThreadView key={activeKey} convKey={activeKey} onBack={split ? undefined : () => openConversation(null)} />
        ) : split ? (
          <div className="text-fg-subtle flex flex-1 flex-col items-center justify-center gap-2 text-sm">
            <MessageSquare className="h-10 w-10 opacity-40" />
            {t('chat.pickConversation')}
          </div>
        ) : null}
      </div>
    </aside>
  );
}
